import { IForecastAccuracyHistory, IForecastAccuracyMonthlyData } from '../domains/entities/ForecastAccuracy';
import {
  GetForecastAccuracyHistoryFromSummaryDataResult,
  CustomerAnnounceSummary,
  SalesResultSummary,
} from '@/coreInfrastructureModule/axios/generated/aggregateBusinessManage';
import { ForecastAccuracyService } from '../domains/services/forecastAccuracyService';
import { GET_FORECAST_ACCURACY_HISTORY_PERIOD } from '../constants/index';

/**
 * 内示正答率生データインターフェース
 * @description APIレスポンスから変換された、フィルタリング前の生データ
 */
export interface IForecastAccuracyRawData {
  /** 取引ID */
  businessId: string;
  /** 客先内示サマリの履歴（フィルタリング前） */
  customerForecastHistory: CustomerAnnounceSummary[];
  /** 販売実績サマリの履歴 */
  salesResultHistory: SalesResultSummary[];
}

/**
 * 内示正答率データのAdapter
 * @description APIレスポンスをドメインモデルに変換する
 */
export class ForecastAccuracyAdapter {
  /**
   * APIレスポンスから内示正答率生データに変換
   * @param businessId - 取引ID
   * @param apiResult - APIレスポンスの結果
   * @returns 内示正答率生データ
   * @throws {Error} 必要なデータが取得できない場合
   * @description periodでのフィルタリングは行わず、生データを返す
   */
  public static adaptToRawData(businessId: string, apiResult: GetForecastAccuracyHistoryFromSummaryDataResult): IForecastAccuracyRawData {
    if (!apiResult?.customerForecastSummaryHistory || !apiResult?.salesResultSummaryHistory) {
      throw new Error('必要なデータが取得できませんでした');
    }
    return {
      businessId,
      customerForecastHistory: apiResult.customerForecastSummaryHistory,
      salesResultHistory: apiResult.salesResultSummaryHistory,
    };
  }

  /**
   * 生データから内示正答率履歴データに変換
   * @param rawData - 内示正答率生データ
   * @param period - 期間（N月前内示を示す）
   * @returns 内示正答率履歴データ
   * @description 生データをperiodでフィルタリングして最終的なビューモデルを作成
   */
  public static adaptToForecastAccuracyHistory(rawData: IForecastAccuracyRawData, period: number): IForecastAccuracyHistory {
    // 期間指定で内示データをフィルタリングして月次データを変換
    const historyData = this.convertToMonthlyData(period, rawData.customerForecastHistory, rawData.salesResultHistory);

    // 平均正答率を計算（全期間のデータを使用）
    const averageAccuracy = ForecastAccuracyService.calculateAverageAccuracy(historyData);

    return {
      businessId: rawData.businessId,
      historyData,
      averageAccuracy,
      selectedPeriod: period,
    };
  }

  /**
   * APIレスポンスからドメインモデルの月次データに変換
   * @param period - 期間（N月前内示を示す）
   * @param customerForecastHistory - 客先内示サマリの履歴
   * @param salesResultHistory - 販売実績サマリの履歴
   * @returns 月次内示正答率データの配列（一年分の連続した12ヶ月のデータを保証）
   */
  private static convertToMonthlyData(
    period: number,
    customerForecastHistory: CustomerAnnounceSummary[],
    salesResultHistory: SalesResultSummary[]
  ): IForecastAccuracyMonthlyData[] {
    // 客先内示データから期間に応じてフィルタリング
    const filteredForecastHistory = this.filterCustomerForecastByPeriod(customerForecastHistory, period);

    // 年月をキーとしたマップを作成
    const salesMap = new Map<string, number>();
    const forecastMap = new Map<string, number>();

    // 販売実績データをマップに格納
    salesResultHistory.forEach((sales) => {
      const yearMonth = `${sales.targetYear}-${String(sales.targetMonth).padStart(2, '0')}`;
      salesMap.set(yearMonth, sales.salesResultAmount || 0);
    });

    // フィルタリングされた客先内示データをマップに格納
    filteredForecastHistory.forEach((forecast) => {
      const yearMonth = `${forecast.customerForecastTargetYear}-${String(forecast.customerForecastTargetMonth).padStart(2, '0')}`;
      const amount = forecast.customerForecastAmountTotal || 0;
      forecastMap.set(yearMonth, amount);
    });

    // 一年分の連続した月データを生成（欠けている月にはゼロ値を設定）
    const historyData: IForecastAccuracyMonthlyData[] = [];
    const currentDate = new Date();

    // 前月から過去12ヶ月分のデータを生成（当月は含まない）
    for (let i = GET_FORECAST_ACCURACY_HISTORY_PERIOD; i >= 1; i--) {
      const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const yearMonth = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;

      // APIデータから値を取得、存在しない場合は0を設定
      const salesResult = salesMap.get(yearMonth) || 0;
      const forecast = forecastMap.get(yearMonth) || 0;

      // Domain Serviceを使用して正答率を計算
      const accuracyRate = ForecastAccuracyService.calculateAccuracyRate(forecast, salesResult);

      historyData.push({
        yearMonth,
        salesResult,
        forecast,
        accuracyRate,
      });
    }

    return historyData;
  }

  /**
   * 期間に応じて客先内示データをフィルタリング
   * @param customerForecastHistory - 客先内示サマリの履歴
   * @param period - 期間（N月前内示を示す）
   * @returns フィルタリングされた客先内示データ
   * @description periodが1の場合、受領月がターゲット月の1ヶ月前のデータのみを返す
   * 例：period=1, receiveMonth=6, targetMonth=7 → 1ヶ月前内示として採用
   */
  private static filterCustomerForecastByPeriod(
    customerForecastHistory: CustomerAnnounceSummary[],
    period: number
  ): CustomerAnnounceSummary[] {
    // periodを確実に数値型に変換（型安全性のため）
    const numericPeriod = Number(period);

    const filtered = customerForecastHistory.filter((forecast) => {
      const receivedYear = forecast.customerForecastReceivedYear;
      const receivedMonth = forecast.customerForecastReceivedMonth;
      const targetYear = forecast.customerForecastTargetYear;
      const targetMonth = forecast.customerForecastTargetMonth;

      // 受領日と対象日の月差を計算
      const receivedDate = new Date(receivedYear, receivedMonth - 1, 1);
      const targetDate = new Date(targetYear, targetMonth - 1, 1);

      // 月差を計算（対象月 - 受領月）
      const monthDiff = (targetDate.getFullYear() - receivedDate.getFullYear()) * 12 + (targetDate.getMonth() - receivedDate.getMonth());

      // N月前内示のデータのみを返す
      return monthDiff === numericPeriod;
    });

    return filtered;
  }
}
